import { useState, useEffect, useCallback } from 'preact/hooks'
import { sidebarStyle, filterBtnStyle } from './shared'
import { TestPanel } from '../TestPanel'
import { FullscreenImageViewer } from '../FullscreenImageViewer'
import { ReportContentDialog, setReportFlagsVisible, useReportFlagsVisible } from '../ReportContentDialog'
import {
  listCloudPersonas,
  loadUserSettings,
  saveUserSettings,
  savePersonaSettings,
  getQuotaStatus,
  getUsageBreakdownForPeriod,
  redeemCoupon,
  deleteAccount,
  generateImage,
  uploadBlob,
  upsertCloudNote,
  webSearch,
  DEFAULT_CHAT_MODEL,
} from '../../services/api'
import type { CloudNote } from '../../types'
import type { CloudPersona, QuotaStatus, PeriodUsageBreakdown } from '../../services/api'
import type { Persona } from '../../types'
import { getThemeAccent } from '../../services/themes'
import { BACKENDS, LOCAL_MODE, enforceBackendAccess, getSelectedBackend, getSelectedBackendOption, setSelectedBackend } from '../../services/backendConfig'
import type { BackendId } from '../../services/backendConfig'
import { loadClientConfig, canUseFullCatalogue, isFreeModel, guardsForTier } from '../../services/clientConfig'
import type { ClientConfig } from '../../services/clientConfig'

interface ThemeCtx {
  theme: string
  darkMode: boolean
  fontSizeIndex: number
  setTheme: (t: string) => void
  setDarkMode: (d: boolean) => void
  setFontSizeIndex: (i: number) => void
}

interface SettingsViewProps {
  personas?: Persona[]
  selectedPersona?: Persona
  themeCtx?: ThemeCtx
  onPersonaSettingsSaved?: () => void
  userEmail?: string
  onSignOut?: () => void | Promise<void>
}

type SettingsCategory = 'account' | 'persona' | 'appearance' | 'ai_services' | 'task_calendar' | 'connection' | 'testing' | 'about'

const CATEGORIES: { key: SettingsCategory; label: string; icon: string }[] = [
  { key: 'account', label: 'Account', icon: '\u{1F464}' },
  { key: 'persona', label: 'AI Personas', icon: '\u{1F916}' },
  { key: 'appearance', label: 'Appearance', icon: '\u{1F3A8}' },
  { key: 'ai_services', label: 'AI Services', icon: '\u{2728}' },
  { key: 'task_calendar', label: 'Task & Calendar', icon: '\u{1F4C5}' },
  { key: 'connection', label: 'Connection', icon: '\u{1F310}' },
  { key: 'testing', label: 'Testing', icon: '\u{1F9EA}' },
  { key: 'about', label: 'About', icon: '\u{2139}\u{FE0F}' },
]

/**
 * Testing tools (running against real backend) require both:
 *   - Owner-tier subscription — so the run can't accidentally pollute / burn
 *     a regular user's account.
 *   - Non-production backend — even owners shouldn't run sweeps on prod data.
 */
function isTestingAllowed(subscriptionTier: string | null): boolean {
  return subscriptionTier === 'owner' && getSelectedBackend() !== 'production'
}

// ── AI Services options ────────────────────────────────────────

// Image model list mirrors mobile's config.dart (`ImageModels`) — keep the two
// in sync. The backend's _execute_create_image dispatches by name prefix
// (gemini-* → Gemini, otherwise OpenAI), so no backend change is needed when
// this list evolves. Stored retired values (e.g. gpt-image-1/1.5/2) are coerced
// to DEFAULT_IMAGE_MODEL below.
const DEFAULT_IMAGE_MODEL = 'gpt-image-2.5-flare' // matches mobile Config.imageGenerationModel
const IMAGE_MODELS: { value: string; label: string }[] = [
  { value: 'gpt-image-2.5-flare', label: 'GPT Image 2.5 Flare' },
  { value: 'gpt-image-2.5-sunburst', label: 'GPT Image 2.5 Sunburst' },
  { value: 'gemini-2.5-flash-image', label: 'Gemini 2.5 Flash Image' },
  { value: 'gemini-3.1-flash-image', label: 'Gemini 3.1 Flash Image' },
  { value: 'gemini-3.1-flash-lite-image', label: 'Gemini 3.1 Flash Lite Image' },
]

const IMAGE_ASPECT_RATIOS: { value: string; label: string }[] = [
  { value: '1:1', label: '1:1 (Square)' },
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '16:9', label: '16:9 (Landscape)' },
]

const WEB_SEARCH_ENGINES: { value: string; label: string }[] = [
  { value: 'openai_search', label: 'OpenAI Search' },
  { value: 'google_search', label: 'Google Search' },
]

// ── Task & Calendar options ────────────────────────────────────

const FIRST_DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
]

const TASK_START_TIMES = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00']

// ── Voice options ──────────────────────────────────────────────

const GEMINI_VOICES = {
  female: ['Achernar', 'Aoede', 'Autonoe', 'Callirrhoe', 'Despina', 'Erinome', 'Gacrux', 'Kore', 'Laomedeia', 'Leda', 'Pulcherrima', 'Sulafat', 'Vindemiatrix', 'Zephyr'],
  male: ['Achird', 'Algenib', 'Algieba', 'Alnilam', 'Charon', 'Enceladus', 'Fenrir', 'Iapetus', 'Orus', 'Puck', 'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Umbriel', 'Zubenelgenubi'],
}

const OPENAI_VOICES = {
  female: ['coral', 'nova', 'sage', 'shimmer', 'ballad', 'marin'],
  male: ['alloy', 'ash', 'echo', 'fable', 'onyx', 'verse', 'cedar'],
}

type ModelTier = 'basic' | 'premium' | 'genius'

interface ModelFamily {
  family: string
  label: string
  tiers: ModelTier[]
  /** Tiers within this family a RESTRICTED subscription may use. Absent means
   *  the whole family is Premium-only. The backend derives the same set from
   *  `model_tiers.PIPAR_TIERS` + the zero-rated aliases; these are the entries
   *  of that set the picker can express as family+tier. */
  restrictedTiers?: ModelTier[]
}

/**
 * Model families and their available tiers (basic=*, premium=**, genius=***).
 *
 * `restrictedTiers` mirrors the server's catalogue gate (model_access.yaml):
 * free and Starter get the PiPar family plus the free open-weight models,
 * while the frontier families are what Premium buys. Families with none are
 * hidden entirely for those tiers rather than shown and refused — the server
 * would reject every message sent with one.
 */
const MODEL_FAMILIES: ModelFamily[] = [
  // In local mode, surface the local OpenAI-compatible model. The backend routes
  // `private-*` model ids to PRIVATE_AI_BASE_URL (see docker-compose.local.yml).
  // `private-*` is ungated server-side — there is no catalogue to gate.
  ...(LOCAL_MODE ? [{ family: 'private', label: 'Local', tiers: ['basic'] as ModelTier[], restrictedTiers: ['basic'] as ModelTier[] }] : []),
  // PiPar tiers lead the list: curated aliases the backend re-points at better
  // models over time, so the user never has to track which provider is best.
  // Hidden in local mode — that is an expert setup where concrete models are
  // chosen deliberately, and the backend omits tiers from /config there too.
  ...(LOCAL_MODE ? [] : [{ family: 'pipar', label: 'PiPar', tiers: ['basic', 'premium', 'genius'] as ModelTier[], restrictedTiers: ['basic', 'premium', 'genius'] as ModelTier[] }]),
  { family: 'claude',   label: 'Claude',   tiers: ['basic', 'premium', 'genius'] },
  { family: 'gemini',   label: 'Gemini',   tiers: ['basic', 'premium', 'genius'] },
  { family: 'openai',   label: 'OpenAI',   tiers: ['basic', 'premium', 'genius'] },
  // Only DeepSeek * is zero-rated (`deepseek-basic`); ** and *** are frontier
  // catalogue like the rest.
  { family: 'deepseek', label: 'DeepSeek', tiers: ['basic', 'premium', 'genius'], restrictedTiers: ['basic'] },
  { family: 'mistral',  label: 'Mistral',  tiers: ['basic', 'premium'] },
  { family: 'qwen',     label: 'Qwen',     tiers: ['basic'] },
  { family: 'gemma',    label: 'Gemma',    tiers: ['basic'], restrictedTiers: ['basic'] },
]

/** The families offered to a subscription tier, with their selectable stars. */
function familiesForTier(fullCatalogue: boolean): ModelFamily[] {
  if (fullCatalogue) return MODEL_FAMILIES
  return MODEL_FAMILIES
    .filter((f) => f.restrictedTiers?.length)
    .map((f) => ({ ...f, tiers: f.restrictedTiers as ModelTier[] }))
}

const TIER_STARS: Record<string, string> = { basic: '\u{2B50}', premium: '\u{2B50}\u{2B50}', genius: '\u{2B50}\u{2B50}\u{2B50}' }
const TIER_LABELS: Record<string, string> = { basic: 'Basic', premium: 'Premium', genius: 'Genius' }
const TIER_COLORS: Record<string, string> = { basic: '#4caf50', premium: '#2196f3', genius: '#9c27b0' }

function getModelFamily(model: string): string {
  for (const suffix of ['-basic', '-premium', '-genius']) {
    if (model.endsWith(suffix)) return model.slice(0, -suffix.length)
  }
  return model
}

function getModelTier(model: string): string {
  if (model.endsWith('-genius')) return 'genius'
  if (model.endsWith('-premium')) return 'premium'
  return 'basic'
}

// ── Main component ────────────────────────────────────────────

export function SettingsView({ personas, selectedPersona, themeCtx, onPersonaSettingsSaved, userEmail, onSignOut }: SettingsViewProps) {
  const [category, setCategory] = useState<SettingsCategory>('persona')
  const [useMobileSettings, setUseMobileSettings] = useState(false)
  const [mobileSettings, setMobileSettings] = useState<Record<string, unknown>>({})
  const [webSettings, setWebSettings] = useState<Record<string, unknown>>({})
  const [cloudPersonas, setCloudPersonas] = useState<CloudPersona[]>([])
  const [editingPersonaId, setEditingPersonaId] = useState(selectedPersona?.id || 'pipar')
  const [personaDraft, setPersonaDraft] = useState<Partial<CloudPersona>>({})
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null)
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null)
  const [creditsLeft, setCreditsLeft] = useState<number | null>(null)

  // Catalogue gate + free-model list. Startup config, fetched once and cached
  // in the service, so this is effectively free on re-mount.
  useEffect(() => {
    let cancelled = false
    loadClientConfig().then((cfg) => { if (!cancelled) setClientConfig(cfg) })
    return () => { cancelled = true }
  }, [])

  // Resolve the user's subscription tier — used to gate owner-only screens
  // (Connection). Conservatively hides them while loading.
  useEffect(() => {
    if (!userEmail) { setSubscriptionTier(null); return }
    let cancelled = false
    getQuotaStatus(userEmail).then((s) => {
      if (cancelled) return
      const tier = s?.subscription_tier?.toLowerCase() || 'free'
      setSubscriptionTier(tier)
      // Credits decide the picker as much as the tier does: a balance buys the
      // full catalogue on any tier, matching the server.
      setCreditsLeft(typeof s?.credits_left === 'number' ? s.credits_left : null)
      enforceBackendAccess(tier)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [userEmail])

  // Load settings and personas on mount
  useEffect(() => {
    loadUserSettings('web').then(setWebSettings).catch(() => {})
    loadUserSettings('mobile').then(setMobileSettings).catch(() => {})
    listCloudPersonas().then((cps) => {
      setCloudPersonas(cps)
    }).catch(() => {})
  }, [])

  // Load "use mobile" toggle from web settings
  useEffect(() => {
    if (webSettings.use_mobile_settings !== undefined) {
      setUseMobileSettings(!!webSettings.use_mobile_settings)
    }
  }, [webSettings])

  // Set editing persona draft when selection changes
  useEffect(() => {
    const cp = cloudPersonas.find((p) => p.id === editingPersonaId)
    if (cp) {
      setPersonaDraft({ ...cp })
    } else {
      setPersonaDraft({ id: editingPersonaId, persona_name: '', tts_voice: '', tts_provider: 'gemini', tts_speaking_style: '', speaking_speed: 1.0, chat_model: '' })
    }
    setSaveMsg(null)
  }, [editingPersonaId, cloudPersonas])

  const activeSettings = useMobileSettings ? mobileSettings : webSettings

  const handleToggleMobile = useCallback(async (val: boolean) => {
    setUseMobileSettings(val)
    await saveUserSettings({ use_mobile_settings: val }, 'web')
  }, [])

  const handleSavePersona = useCallback(async () => {
    if (!personaDraft.id) return
    setSaving(true)
    setSaveMsg(null)
    const ok = await savePersonaSettings({
      id: personaDraft.id,
      persona_name: personaDraft.persona_name,
      chat_model: personaDraft.chat_model,
      tts_voice: personaDraft.tts_voice,
      tts_provider: personaDraft.tts_provider,
      tts_speaking_style: personaDraft.tts_speaking_style,
      speaking_speed: personaDraft.speaking_speed,
    })
    setSaving(false)
    setSaveMsg(ok ? 'Saved' : 'Failed to save')
    if (ok) {
      // Refresh local settings list and notify App to update ttsSettings/chatModel
      listCloudPersonas().then(setCloudPersonas).catch(() => {})
      if (onPersonaSettingsSaved) onPersonaSettingsSaved()
    }
    setTimeout(() => setSaveMsg(null), 3000)
  }, [personaDraft, onPersonaSettingsSaved])

  const handleSaveAppearance = useCallback(async (key: string, value: unknown) => {
    const updated = { ...webSettings, [key]: value }
    setWebSettings(updated)
    await saveUserSettings({ [key]: value }, 'web')
  }, [webSettings])

  const updateDraft = (key: string, value: unknown) => {
    setPersonaDraft((prev) => ({ ...prev, [key]: value }))
    setSaveMsg(null)
  }

  // Testing only available against the dev backend — running tool tests on
  // production would burn the user's credits and pollute their data.
  const testingAllowed = isTestingAllowed(subscriptionTier)
  // Connection picker is admin-only — users on free/basic/premium have no reason
  // to swap backends and can do real damage by flipping it. Local mode always
  // exposes it so the local backend can be selected without an owner account.
  const connectionAllowed = subscriptionTier === 'owner' || LOCAL_MODE
  // Voice fillers are still being tried out: the toggle is for owners only.
  // Everyone else gets the backend's default (on, where the server enables them).
  const voiceFillerAllowed = subscriptionTier === 'owner'

  let visibleCategories = CATEGORIES
  if (!testingAllowed) visibleCategories = visibleCategories.filter((c) => c.key !== 'testing')
  if (!connectionAllowed) visibleCategories = visibleCategories.filter((c) => c.key !== 'connection')

  // If the user lands on a now-hidden category, fall back to Account.
  const effectiveCategory: SettingsCategory =
    (category === 'testing' && !testingAllowed) ||
    (category === 'connection' && !connectionAllowed)
      ? 'account'
      : category

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Category sidebar */}
      <div style={sidebarStyle}>
        <div style={{ padding: 12 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: 0.5, marginBottom: 8 }}>Settings</div>
          {visibleCategories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              style={{ ...filterBtnStyle(effectiveCategory === cat.key), display: 'flex', gap: 8, width: '100%', textAlign: 'left' }}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Settings content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
          {CATEGORIES.find((c) => c.key === effectiveCategory)?.icon}{' '}
          {CATEGORIES.find((c) => c.key === effectiveCategory)?.label}
        </h2>

        {/* Use Mobile Settings toggle — shown in persona, appearance, AI services, task/calendar */}
        {(effectiveCategory === 'persona' || effectiveCategory === 'appearance' || effectiveCategory === 'ai_services' || effectiveCategory === 'task_calendar') && (
          <SettingsSection title="Settings Source">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <ToggleSwitch checked={useMobileSettings} onChange={handleToggleMobile} />
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                Use mobile app settings {useMobileSettings ? '(read-only)' : '(off — using web settings)'}
              </span>
              {/* Voice fillers in web calls: a web setting, sent as voice_filler when a call starts. */}
              {effectiveCategory === 'persona' && voiceFillerAllowed && (
                <>
                  <span style={{ width: 32 }} />
                  <ToggleSwitch
                    checked={webSettings.voice_filler !== false}
                    onChange={(val) => void handleSaveAppearance('voice_filler', val)}
                  />
                  <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                    Quick replies while the AI thinks
                  </span>
                </>
              )}
            </div>
          </SettingsSection>
        )}

        {effectiveCategory === 'account' && (
          <AccountSettings userEmail={userEmail || ''} onSignOut={onSignOut} />
        )}

        {effectiveCategory === 'persona' && (
          <PersonaSettings
            personas={personas || []}
            cloudPersonas={cloudPersonas}
            editingPersonaId={editingPersonaId}
            onSelectPersona={setEditingPersonaId}
            draft={personaDraft}
            onUpdateDraft={updateDraft}
            onSave={handleSavePersona}
            saving={saving}
            saveMsg={saveMsg}
            readOnly={useMobileSettings}
            subscriptionTier={subscriptionTier}
            creditsLeft={creditsLeft}
            clientConfig={clientConfig}
          />
        )}

        {effectiveCategory === 'appearance' && (
          <AppearanceSettings
            settings={activeSettings}
            onSave={handleSaveAppearance}
            readOnly={useMobileSettings}
            themeCtx={themeCtx}
          />
        )}

        {effectiveCategory === 'ai_services' && (
          <AiServicesSettings
            settings={activeSettings}
            onSave={handleSaveAppearance}
            readOnly={useMobileSettings}
          />
        )}

        {effectiveCategory === 'task_calendar' && (
          <TaskCalendarSettings
            settings={activeSettings}
            onSave={handleSaveAppearance}
            readOnly={useMobileSettings}
          />
        )}

        {effectiveCategory === 'connection' && connectionAllowed && (
          <ConnectionSettings />
        )}

        {effectiveCategory === 'testing' && testingAllowed && (
          <div style={{ margin: '-24px', height: 'calc(100% + 48px)' }}>
            <TestPanel
              personas={personas || [{ id: 'pipar', name: 'PiPar', emoji: '\u{1F43B}\u{200D}\u{2744}\uFE0F}' }]}
              selectedPersona={selectedPersona || { id: 'pipar', name: 'PiPar', emoji: '\u{1F43B}\u{200D}\u{2744}\uFE0F}' }}
            />
          </div>
        )}

        {effectiveCategory === 'about' && (
          <AboutPanel />
        )}
      </div>
    </div>
  )
}

// ── Persona Settings Panel ────────────────────────────────────

function PersonaSettings({
  personas, cloudPersonas, editingPersonaId, onSelectPersona,
  draft, onUpdateDraft, onSave, saving, saveMsg, readOnly,
  subscriptionTier, creditsLeft, clientConfig,
}: {
  personas: Persona[]
  cloudPersonas: CloudPersona[]
  editingPersonaId: string
  onSelectPersona: (id: string) => void
  draft: Partial<CloudPersona>
  onUpdateDraft: (key: string, value: unknown) => void
  onSave: () => void
  saving: boolean
  saveMsg: string | null
  readOnly: boolean
  subscriptionTier: string | null
  creditsLeft: number | null
  clientConfig: ClientConfig | null
}) {
  const ttsProvider = draft.tts_provider || 'gemini'
  const voices = ttsProvider === 'openai' ? OPENAI_VOICES : GEMINI_VOICES
  const allVoices = [...voices.female, ...voices.male]

  return (
    <div>
      {/* Persona selector */}
      <SettingsSection title="Select Persona">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {personas.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelectPersona(p.id)}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: editingPersonaId === p.id ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: editingPersonaId === p.id ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      </SettingsSection>

      {/* Persona name */}
      <SettingsSection title="Display Name">
        <input
          type="text"
          value={draft.persona_name || ''}
          onInput={(e) => onUpdateDraft('persona_name', (e.target as HTMLInputElement).value)}
          disabled={readOnly}
          style={inputStyle}
          placeholder="Persona display name"
        />
      </SettingsSection>

      {/* Chat model — family selector + tier stars */}
      <SettingsSection title="Chat Model">
        <ModelSelector
          value={draft.chat_model || DEFAULT_CHAT_MODEL}
          onChange={(val) => onUpdateDraft('chat_model', val)}
          readOnly={readOnly}
          subscriptionTier={subscriptionTier}
          creditsLeft={creditsLeft}
          clientConfig={clientConfig}
        />
      </SettingsSection>

      {/* TTS Provider */}
      <SettingsSection title="TTS Provider">
        <div style={{ display: 'flex', gap: 8 }}>
          {(['gemini', 'geminiFlash', 'openai'] as const).map((prov) => (
            <button
              key={prov}
              onClick={() => !readOnly && onUpdateDraft('tts_provider', prov)}
              disabled={readOnly}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: ttsProvider === prov ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: ttsProvider === prov ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
                color: 'var(--text)',
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 14,
                textTransform: 'capitalize',
              }}
            >
              {prov === 'gemini' ? 'Google Gemini' : prov === 'geminiFlash' ? 'Gemini Flash' : 'OpenAI'}
            </button>
          ))}
        </div>
      </SettingsSection>

      {/* TTS Voice */}
      <SettingsSection title="Voice">
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>Female</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {voices.female.map((v) => (
            <button
              key={v}
              onClick={() => !readOnly && onUpdateDraft('tts_voice', v)}
              disabled={readOnly}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: draft.tts_voice === v ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: draft.tts_voice === v ? 'var(--system-bubble)' : 'transparent',
                color: draft.tts_voice === v ? '#FAFAFA' : 'rgba(250,250,250,0.6)',
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 12,
              }}
            >
              {v}
            </button>
          ))}
        </div>
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>Male</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {voices.male.map((v) => (
            <button
              key={v}
              onClick={() => !readOnly && onUpdateDraft('tts_voice', v)}
              disabled={readOnly}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: draft.tts_voice === v ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: draft.tts_voice === v ? 'var(--system-bubble)' : 'transparent',
                color: draft.tts_voice === v ? '#FAFAFA' : 'rgba(250,250,250,0.6)',
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 12,
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </SettingsSection>

      {/* Speaking style */}
      <SettingsSection title="Speaking Style Instructions">
        <textarea
          value={draft.tts_speaking_style || ''}
          onInput={(e) => onUpdateDraft('tts_speaking_style', (e.target as HTMLTextAreaElement).value)}
          disabled={readOnly}
          style={{ ...inputStyle, maxWidth: '100%', minHeight: 60, resize: 'vertical' }}
          placeholder="e.g. Warm, friendly tone. Speak clearly and at a moderate pace."
        />
      </SettingsSection>

      {/* Speaking speed */}
      <SettingsSection title={`Speaking Speed: ${(draft.speaking_speed ?? 1.0).toFixed(2)}x`}>
        <input
          type="range"
          min="0.25"
          max="4.0"
          step="0.25"
          value={draft.speaking_speed ?? 1.0}
          onInput={(e) => onUpdateDraft('speaking_speed', parseFloat((e.target as HTMLInputElement).value))}
          disabled={readOnly}
          style={{ width: '100%', accentColor: 'var(--primary)' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)' }}>
          <span>0.25x</span><span>1.0x</span><span>4.0x</span>
        </div>
      </SettingsSection>

      {/* Save button */}
      {!readOnly && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              padding: '10px 28px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--primary)',
              color: '#fff',
              cursor: saving ? 'default' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Persona Settings'}
          </button>
          {saveMsg && (
            <span style={{ fontSize: 13, color: saveMsg === 'Saved' ? '#4caf50' : '#f44336' }}>{saveMsg}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Appearance Settings Panel ─────────────────────────────────

const THEMES = [
  { value: 'sea', label: 'Sea' },
  { value: 'winter', label: 'Winter' },
  { value: 'greyscale', label: 'Greyscale' },
  { value: 'forest', label: 'Forest' },
  { value: 'desert', label: 'Desert' },
  { value: 'rose', label: 'Rose' },
  { value: 'mocha', label: 'Mocha' },
  { value: 'rainbow', label: 'Rainbow' },
]

const TEXT_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fi', label: 'Suomi' },
  { value: 'fr', label: 'Fran\u00E7ais' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Espa\u00F1ol' },
]

const VOICE_LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'fi-FI', label: 'Finnish' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'es-ES', label: 'Spanish' },
]

function AppearanceSettings({
  settings, onSave, readOnly, themeCtx,
}: {
  settings: Record<string, unknown>
  onSave: (key: string, value: unknown) => void
  readOnly: boolean
  themeCtx?: ThemeCtx
}) {
  const themeType = themeCtx?.theme || (settings.theme_type as string) || 'sea'
  const darkMode = themeCtx?.darkMode ?? (settings.dark_mode !== undefined ? !!settings.dark_mode : true)
  const fontSizeIndex = themeCtx?.fontSizeIndex ?? (typeof settings.font_size_index === 'number' ? settings.font_size_index : 2)
  const fontSizeLabels = ['XS', 'S', 'M', 'L', 'XL']
  const textLanguage = (settings.text_language as string) || 'en'
  const voiceLanguage = (settings.voice_language as string) || 'en-US'

  return (
    <div>
      {/* Theme */}
      <SettingsSection title="Theme">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {THEMES.map((t) => (
            <button
              key={t.value}
              disabled={readOnly}
              onClick={() => {
                if (readOnly) return
                if (themeCtx) themeCtx.setTheme(t.value)
                else onSave('theme_type', t.value)
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: themeType === t.value ? `2px solid ${getThemeAccent(t.value)}` : '1px solid var(--border)',
                background: themeType === t.value ? `${getThemeAccent(t.value)}33` : 'var(--bg-surface)',
                color: 'var(--text)',
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 13,
              }}
            >
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: getThemeAccent(t.value), marginRight: 8, verticalAlign: 'middle' }} />
              {t.label}
            </button>
          ))}
        </div>
      </SettingsSection>

      {/* Dark Mode */}
      <SettingsSection title="Dark Mode">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ToggleSwitch
            checked={darkMode}
            onChange={(val) => {
              if (readOnly) return
              if (themeCtx) themeCtx.setDarkMode(val)
              else onSave('dark_mode', val)
            }}
            disabled={readOnly}
          />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{darkMode ? 'On' : 'Off'}</span>
        </div>
      </SettingsSection>

      {/* Font Size */}
      <SettingsSection title={`Font Size: ${fontSizeLabels[fontSizeIndex] || 'M'}`}>
        <div style={{ display: 'flex', gap: 8 }}>
          {fontSizeLabels.map((label, i) => (
            <button
              key={label}
              onClick={() => {
                if (readOnly) return
                if (themeCtx) themeCtx.setFontSizeIndex(i)
                else onSave('font_size_index', i)
              }}
              disabled={readOnly}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: fontSizeIndex === i ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: fontSizeIndex === i ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
                color: 'var(--text)',
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 14,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </SettingsSection>

      {/* Text Language */}
      <SettingsSection title="Text Language">
        <select
          value={textLanguage}
          onChange={(e) => !readOnly && onSave('text_language', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {TEXT_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </SettingsSection>

      {/* Voice Language */}
      <SettingsSection title="Voice Language">
        <select
          value={voiceLanguage}
          onChange={(e) => !readOnly && onSave('voice_language', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {VOICE_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </SettingsSection>
    </div>
  )
}

// ── AI Services Settings Panel ────────────────────────────────

function AiServicesSettings({
  settings, onSave, readOnly,
}: {
  settings: Record<string, unknown>
  onSave: (key: string, value: unknown) => void
  readOnly: boolean
}) {
  // Read `image_model` (canonical — matches mobile + backend tool executor
  // which reads `client_settings["image_model"]`). Fall back to
  // `image_gen_model` for any values persisted under the desktop key.
  // If the stored value is no longer in IMAGE_MODELS (e.g. legacy "dall-e",
  // "gemini-flash"), coerce to the current default so the <select> isn't blank.
  const storedImageModel = (settings.image_model as string) || (settings.image_gen_model as string) || ''
  const imageModel = IMAGE_MODELS.some((m) => m.value === storedImageModel)
    ? storedImageModel
    : DEFAULT_IMAGE_MODEL
  const aspectRatio = (settings.image_aspect_ratio as string) || '1:1'
  const searchEngine = (settings.web_search_engine as string) || 'openai_search'

  return (
    <div>
      <SettingsSection title="Image Generation Model">
        <select
          value={imageModel}
          onChange={(e) => !readOnly && onSave('image_model', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {IMAGE_MODELS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </SettingsSection>

      <SettingsSection title="Image Aspect Ratio">
        <select
          value={aspectRatio}
          onChange={(e) => !readOnly && onSave('image_aspect_ratio', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {IMAGE_ASPECT_RATIOS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </SettingsSection>

      <DrawImageSection model={imageModel} aspectRatio={aspectRatio} />

      <SettingsSection title="Web Search Engine">
        <select
          value={searchEngine}
          onChange={(e) => !readOnly && onSave('web_search_engine', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {WEB_SEARCH_ENGINES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </SettingsSection>

      <WebSearchSection searchEngine={searchEngine} />
    </div>
  )
}

// ── Draw Image (uses the image model + aspect ratio above) ────

function DrawImageSection({ model, aspectRatio }: { model: string; aspectRatio: string }) {
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [imageData, setImageData] = useState<string | null>(null)
  const [revisedPrompt, setRevisedPrompt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportSent, setReportSent] = useState(false)

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || generating) return
    setGenerating(true)
    setError(null)
    setImageData(null)
    setRevisedPrompt(null)
    setSaveMsg(null)
    setReportSent(false)
    const result = await generateImage(prompt, model, aspectRatio)
    setGenerating(false)
    if (result.success && result.imageData) {
      setImageData(result.imageData)
      setRevisedPrompt(result.revisedPrompt || null)
    } else {
      setError(result.error || 'Failed to generate image')
    }
  }, [prompt, model, aspectRatio, generating])

  // Save to cloud as an AI-generated image note (shows up in App Images on mobile).
  const handleSaveToApp = useCallback(async () => {
    if (!imageData || saving) return
    setSaving(true)
    setSaveMsg(null)
    try {
      // base64 → Uint8Array
      const binary = atob(imageData)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)

      const noteId = crypto.randomUUID()
      const attachmentId = crypto.randomUUID()
      const fullPrompt = prompt.trim()
      const title = fullPrompt.length > 50 ? `${fullPrompt.slice(0, 47)}…` : (fullPrompt || 'AI Generated Image')
      const slug = (fullPrompt.slice(0, 40).replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '')) || 'image'
      const filename = `${slug}_${Date.now()}.png`

      // Step 1: upload the actual PNG bytes to backend blob storage (R2).
      setSaveMsg({ ok: true, text: `Uploading image to backend (${(bytes.length / 1024).toFixed(0)} KB)…` })
      const upload = await uploadBlob(bytes, filename, 'image/png', 'notes', noteId)
      if (!upload.ok || !upload.key) {
        setSaveMsg({
          ok: false,
          text: upload.status
            ? `Upload failed (${upload.status}): ${upload.error || 'unknown error'}`
            : `Upload failed: ${upload.error || 'unknown error'}`,
        })
        return
      }

      // Step 2: create the note record pointing at the uploaded blob.
      setSaveMsg({ ok: true, text: 'Saving note record…' })
      const now = new Date().toISOString()
      const note: CloudNote = {
        id: noteId,
        title,
        type: 2, // NoteType.image
        tags: ['AI Generated', 'Image'],
        is_ai_generated_image: true,
        attachments: [
          {
            id: attachmentId,
            type: 0, // AttachmentType.image
            blob_key: upload.key,
            description: fullPrompt || undefined,
            metadata: { model, aspect_ratio: aspectRatio, prompt: fullPrompt, content_type: 'image/png', size: upload.size },
          },
        ],
        created_at: now,
        last_updated_at: now,
      }
      await upsertCloudNote(note)
      setSaveMsg({ ok: true, text: `Saved — image uploaded (${upload.size ? `${(upload.size / 1024).toFixed(0)} KB` : 'OK'}) and note created.` })
    } catch (e) {
      setSaveMsg({ ok: false, text: `Save failed: ${(e as Error).message}` })
    } finally {
      setSaving(false)
    }
  }, [imageData, saving, prompt, model, aspectRatio])

  const dataUrl = imageData ? `data:image/png;base64,${imageData}` : null
  const downloadName = (() => {
    const slug = prompt.trim().slice(0, 40).replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'image'
    return `${slug}_${Date.now()}.png`
  })()

  return (
    <SettingsSection title="Draw Image">
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Uses the model and aspect ratio selected above ({model} · {aspectRatio}).
      </div>
      <textarea
        value={prompt}
        onInput={(e) => setPrompt((e.target as HTMLTextAreaElement).value)}
        placeholder="Describe the image you want to generate…"
        disabled={generating}
        style={{ ...inputStyle, maxWidth: '100%', minHeight: 70, resize: 'vertical', marginBottom: 10 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={handleGenerate}
          disabled={!prompt.trim() || generating}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--primary)',
            color: '#fff',
            cursor: (!prompt.trim() || generating) ? 'default' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            opacity: (!prompt.trim() || generating) ? 0.6 : 1,
          }}
        >
          {generating ? 'Generating…' : 'Generate'}
        </button>
        {imageData && (
          <button
            onClick={() => setFullscreen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            View Fullscreen
          </button>
        )}
        {imageData && (
          <button
            onClick={handleSaveToApp}
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid var(--primary)',
              background: 'transparent',
              color: 'var(--primary)',
              cursor: saving ? 'default' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save to App Images'}
          </button>
        )}
        {dataUrl && (
          <a
            href={dataUrl}
            download={downloadName}
            style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
          >
            Download PNG
          </a>
        )}
        {imageData && (
          <button
            onClick={() => { setImageData(null); setRevisedPrompt(null); setError(null); setSaveMsg(null) }}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Clear
          </button>
        )}
        {imageData && (
          // Report offensive AI output in-app (Google Play AI content policy)
          <button
            onClick={() => setReportOpen(true)}
            disabled={reportSent}
            title="Report inappropriate content"
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid rgba(244,67,54,0.4)',
              background: 'transparent',
              color: '#f44336',
              cursor: reportSent ? 'default' : 'pointer',
              fontSize: 13,
              opacity: reportSent ? 0.6 : 1,
            }}
          >
            {reportSent ? '✓ Reported' : '\u{1F6A9} Report'}
          </button>
        )}
      </div>

      {reportOpen && dataUrl && (
        <ReportContentDialog
          kind="image"
          aiContent={
            revisedPrompt && revisedPrompt !== prompt
              ? `${prompt}\n\nRevised: ${revisedPrompt}`
              : prompt
          }
          // The prompt here is the user's own, so it is also their request.
          userRequest={prompt}
          imageUrl={dataUrl}
          onClose={(sent) => {
            setReportOpen(false)
            if (sent) setReportSent(true)
          }}
        />
      )}

      {saveMsg && (
        <div style={{ marginTop: 10, fontSize: 13, color: saveMsg.ok ? '#4caf50' : '#f44336' }}>{saveMsg.text}</div>
      )}

      {error && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#f44336' }}>{error}</div>
      )}

      {dataUrl && (
        <div style={{ marginTop: 14 }}>
          <img
            src={dataUrl}
            alt={prompt}
            onClick={() => setFullscreen(true)}
            title="Click to view fullscreen"
            style={{
              maxWidth: '100%',
              maxHeight: 480,
              borderRadius: 8,
              border: '1px solid var(--border)',
              display: 'block',
              cursor: 'zoom-in',
            }}
          />
          {revisedPrompt && revisedPrompt !== prompt && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
              <strong>Revised prompt:</strong> {revisedPrompt}
            </div>
          )}
        </div>
      )}

      {fullscreen && dataUrl && (
        <FullscreenImageViewer src={dataUrl} alt={prompt} onClose={() => setFullscreen(false)} />
      )}
    </SettingsSection>
  )
}

// ── Web Search test (uses the engine selected above) ──────────

function WebSearchSection({ searchEngine }: { searchEngine: string }) {
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<string | null>(null)
  const [sources, setSources] = useState<Array<{ uri?: string; title?: string }> | null>(null)
  const [model, setModel] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [duration, setDuration] = useState<number | null>(null)

  const handleSearch = useCallback(async () => {
    if (!query.trim() || searching) return
    setSearching(true)
    setError(null)
    setResults(null)
    setSources(null)
    setModel(null)
    setDuration(null)
    const start = performance.now()
    const res = await webSearch(query, searchEngine)
    const elapsed = performance.now() - start
    setSearching(false)
    setDuration(elapsed)
    if (res.success) {
      setResults(res.results || '(no results text)')
      setSources(res.sources || null)
      setModel(res.model || null)
    } else {
      setError(res.error || 'Search failed')
    }
  }, [query, searchEngine, searching])

  return (
    <SettingsSection title="Test Search">
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        Sends a query through the selected engine ({searchEngine}) and shows the response.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
          placeholder="Enter a search query…"
          disabled={searching}
          style={{ ...inputStyle, flex: 1, minWidth: 200, maxWidth: 'none' }}
        />
        <button
          onClick={handleSearch}
          disabled={!query.trim() || searching}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--primary)',
            color: '#fff',
            cursor: (!query.trim() || searching) ? 'default' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            opacity: (!query.trim() || searching) ? 0.6 : 1,
          }}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 13, color: '#f44336', marginTop: 8 }}>{error}</div>
      )}

      {results !== null && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
            {model && <span>Model: <code>{model}</code> · </span>}
            {duration !== null && <span>{(duration / 1000).toFixed(1)}s</span>}
          </div>
          <div style={{
            ...settingValueStyle,
            padding: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 360,
            overflow: 'auto',
            fontSize: 13,
            lineHeight: 1.5,
          }}>
            {results}
          </div>
          {sources && sources.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Sources ({sources.length})
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12 }}>
                {sources.map((s, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {s.uri ? (
                      <a href={s.uri} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                        {s.title || s.uri}
                      </a>
                    ) : (
                      <span>{s.title || '(unnamed source)'}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SettingsSection>
  )
}

// ── Task & Calendar Settings Panel ────────────────────────────

function TaskCalendarSettings({
  settings, onSave, readOnly,
}: {
  settings: Record<string, unknown>
  onSave: (key: string, value: unknown) => void
  readOnly: boolean
}) {
  const firstDayRaw = settings.first_day_of_week
  const firstDay = typeof firstDayRaw === 'number'
    ? firstDayRaw
    : typeof firstDayRaw === 'string' && firstDayRaw !== ''
      ? Number(firstDayRaw)
      : 1
  const rawStart = (settings.default_task_start_time as string) || '09:00'
  const startTime = TASK_START_TIMES.includes(rawStart) ? rawStart : '09:00'

  return (
    <div>
      <SettingsSection title="First Day of Week">
        <select
          value={String(firstDay)}
          onChange={(e) => !readOnly && onSave('first_day_of_week', Number((e.target as HTMLSelectElement).value))}
          disabled={readOnly}
          style={inputStyle}
        >
          {FIRST_DAY_OPTIONS.map((d) => (
            <option key={d.value} value={String(d.value)}>{d.label}</option>
          ))}
        </select>
      </SettingsSection>

      <SettingsSection title="Default Task Start Time">
        <select
          value={startTime}
          onChange={(e) => !readOnly && onSave('default_task_start_time', (e.target as HTMLSelectElement).value)}
          disabled={readOnly}
          style={inputStyle}
        >
          {TASK_START_TIMES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </SettingsSection>

      <SettingsSection title="Alert Tone">
        <div style={{ ...settingValueStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ opacity: 0.7 }}>Default</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Planned</span>
        </div>
      </SettingsSection>
    </div>
  )
}

// ── Model Selector (family + tier) ─────────────────────────────

function ModelSelector({
  value,
  onChange,
  readOnly,
  subscriptionTier,
  creditsLeft,
  clientConfig,
}: {
  value: string
  onChange: (v: string) => void
  readOnly: boolean
  /** Null while the tier is still loading — read as restricted. */
  subscriptionTier: string | null
  /** Spendable balance. Null while loading — read as none, so the list grows
   *  when it arrives rather than shrinking under the user. */
  creditsLeft: number | null
  clientConfig: ClientConfig | null
}) {
  // Credits win, exactly as on the server: a balance buys the whole catalogue
  // on any tier. Hiding models the user has already paid to be able to use
  // would sell them credits and then refuse to spend them.
  const fullCatalogue = canUseFullCatalogue(subscriptionTier, creditsLeft, clientConfig)
  const families = familiesForTier(fullCatalogue)

  const currentFamily = getModelFamily(value)
  const currentTier = getModelTier(value)
  const familyDef =
    families.find((f) => f.family === currentFamily) || families[0]

  // A stored selection outside this tier's catalogue — a lapsed Premium
  // subscription, or a persona synced from a device that could pick it. The
  // server refuses it on every message, so say so and offer the way back
  // rather than silently rendering the first family's button as selected.
  const outOfTier = !families.some((f) => f.family === currentFamily && f.tiers.includes(currentTier as ModelTier))
  const free = isFreeModel(value, subscriptionTier, clientConfig)
  const guards = guardsForTier(subscriptionTier, clientConfig)

  const setFamily = (family: string) => {
    const def = families.find((f) => f.family === family)
    if (!def) return
    // Keep current tier if available, otherwise use first available
    const tier = def.tiers.includes(currentTier as ModelTier) ? currentTier : def.tiers[0]
    onChange(`${family}-${tier}`)
  }

  const cycleTier = () => {
    // Out-of-tier selections cycle within the FALLBACK family, not their own:
    // `${currentFamily}-${next}` on a hidden family (claude-premium ->
    // claude-genius) would just pick a second model the server also refuses.
    const family = outOfTier ? familyDef.family : currentFamily
    const idx = familyDef.tiers.indexOf(currentTier as ModelTier)
    const next = familyDef.tiers[outOfTier ? 0 : (idx + 1) % familyDef.tiers.length]
    onChange(`${family}-${next}`)
  }

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Family buttons */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {families.map((f) => (
          <button
            key={f.family}
            onClick={() => !readOnly && setFamily(f.family)}
            disabled={readOnly}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: currentFamily === f.family ? '2px solid var(--primary)' : '1px solid var(--border)',
              background: currentFamily === f.family ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
              color: 'var(--text)',
              cursor: readOnly ? 'default' : 'pointer',
              fontSize: 13,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tier indicator — clickable to cycle. Out of tier, it becomes the way
          back: one click moves the selection onto the fallback family. */}
      {familyDef.tiers.length > 1 ? (
        <button
          onClick={() => !readOnly && cycleTier()}
          disabled={readOnly}
          title={`${TIER_LABELS[currentTier]} — click to cycle`}
          style={{
            padding: '4px 12px',
            borderRadius: 12,
            border: `1px solid ${TIER_COLORS[currentTier] || '#888'}`,
            background: `${TIER_COLORS[currentTier] || '#888'}33`,
            color: TIER_COLORS[currentTier] || '#888',
            cursor: readOnly ? 'default' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {TIER_STARS[currentTier]} {TIER_LABELS[currentTier]}
        </button>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 8px' }}>
          {TIER_STARS[currentTier]} {TIER_LABELS[currentTier]}
        </span>
      )}

      {/* Free badge. Only shown when the model really is zero-rated for THIS
          tier — the server's answer, not a guess from the model id. */}
      {free && (
        <span
          title={
            guards.window_messages && guards.weekly_messages
              ? `Free: ${guards.window_messages} messages per ${guards.window_hours ?? 5}h, ${guards.weekly_messages} per week`
              : 'Costs no credits'
          }
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 12,
            border: '1px solid #4caf50',
            background: '#4caf5022',
            color: '#4caf50',
            whiteSpace: 'nowrap',
          }}
        >
          Free
        </span>
      )}

      {/* Out-of-tier selection — see `outOfTier` above. */}
      {outOfTier && (
        <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text)' }}>{value}</strong> needs credits or a
          Premium plan, and your balance is empty — it will be refused. Pick one of the
          models above, or add credits to keep using it.
        </div>
      )}

      {/* Why the list is short, and both ways to widen it. Not shown to a user
          who already has everything — a limit nobody is near reads as a
          restriction where none was felt. */}
      {!fullCatalogue && !outOfTier && (
        <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Claude, Gemini and OpenAI models are included with Premium, or run on
          credits at cost on any plan.
        </div>
      )}
    </div>
  )
}

// ── Connection Settings Panel ─────────────────────────────────

function ConnectionSettings() {
  const currentId = getSelectedBackend()
  const isDev = import.meta.env.DEV

  const handleSelect = (id: BackendId) => {
    if (id === currentId) return
    const target = BACKENDS.find((b) => b.id === id)
    const confirmed = window.confirm(
      `Switch backend to "${target?.label}"?\n\nThe page will reload.`,
    )
    if (confirmed) setSelectedBackend(id)
  }

  return (
    <div>
      <SettingsSection title="Backend">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {BACKENDS.map((b) => {
            const selected = b.id === currentId
            return (
              <button
                key={b.id}
                onClick={() => handleSelect(b.id)}
                style={{
                  padding: '12px 14px',
                  borderRadius: 8,
                  border: selected ? '2px solid #2DA5A0' : '1px solid var(--border)',
                  background: selected ? 'var(--assistant-bubble)' : 'var(--bg-surface)',
                  color: 'var(--text)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  border: selected ? 'none' : '2px solid var(--border)',
                  background: selected ? '#2DA5A0' : 'transparent',
                  flexShrink: 0,
                }} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 600 }}>{b.label.split(' (')[0]}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.url}</span>
                </span>
              </button>
            )
          })}
        </div>
        {isDev && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
            Note: in dev mode requests go through the Vite proxy, which forwards each backend
            under its own <code>/be-&lt;id&gt;</code> prefix. Switching here reloads the page and takes
            effect immediately — no Vite restart needed.
          </div>
        )}
      </SettingsSection>

      <SettingsSection title="Status">
        <div style={{ ...settingValueStyle, color: '#4caf50' }}>Connected</div>
      </SettingsSection>
    </div>
  )
}

// ── About Panel ──────────────────────────────────────────────

function AboutPanel() {
  // Used only for the copyright footer.
  const year = new Date().getFullYear()
  const reportFlagsVisible = useReportFlagsVisible()

  const linkRowStyle: Record<string, string | number> = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    textDecoration: 'none',
    fontSize: 14,
    marginBottom: 10,
  }

  return (
    <div>
      {/* Hero card — PiPar logo, name, tagline, version pill */}
      <div style={{
        textAlign: 'center',
        padding: '32px 16px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        marginBottom: 20,
      }}>
        <img
          src="/splash_icon_colorful.png"
          alt="PiPar"
          style={{ width: 96, height: 96, objectFit: 'contain', marginBottom: 12 }}
        />
        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>PiPar</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Your Digital Business Partner
        </div>
        <div style={{
          display: 'inline-block',
          padding: '4px 14px',
          borderRadius: 16,
          background: 'rgba(45,165,160,0.15)',
          color: 'var(--primary)',
          fontSize: 13,
          fontWeight: 600,
        }}>
          Web Client {__APP_VERSION__}
        </div>
      </div>

      {/* External links — mirror the mobile Support screen */}
      <SettingsSection title="Support & Legal">
        <a href="https://piparapp.com/contact.html" target="_blank" rel="noopener noreferrer" style={linkRowStyle}>
          <span style={{ fontSize: 18 }}>{'\u{2753}'}</span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Help & Support</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Contact us, FAQs, troubleshooting</div>
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{'\u{2197}'}</span>
        </a>
        <a href="https://piparapp.com/privacy.html" target="_blank" rel="noopener noreferrer" style={linkRowStyle}>
          <span style={{ fontSize: 18 }}>{'\u{1F512}'}</span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Privacy Policy</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>How we collect and use your data</div>
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{'\u{2197}'}</span>
        </a>
        <a href="https://piparapp.com/terms_of_service.html" target="_blank" rel="noopener noreferrer" style={linkRowStyle}>
          <span style={{ fontSize: 18 }}>{'\u{1F4C4}'}</span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Terms of Service</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Terms and conditions for using PiPar</div>
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{'\u{2197}'}</span>
        </a>
        {/* Show / hide the report flags on AI replies — mobile parity
            (Settings › Support). The Report menu item and buttons stay. */}
        <div style={{ ...linkRowStyle, cursor: 'default' }}>
          <span style={{ fontSize: 18 }}>{'\u{1F6A9}'}</span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>Show report flags</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Show the red flag on AI responses for reporting inappropriate content. When off, you can still report from the message menu and the Report buttons.
            </div>
          </span>
          <ToggleSwitch checked={reportFlagsVisible} onChange={setReportFlagsVisible} />
        </div>
      </SettingsSection>

      {/* Need help? card — mobile parity */}
      <SettingsSection title="Need help?">
        <div style={settingValueStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 13 }}>
            <span style={{ width: 18, textAlign: 'center' }}>{'\u{2709}'}</span>
            <a href="mailto:support@piparapp.com" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
              support@piparapp.com
            </a>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{ width: 18, textAlign: 'center' }}>{'\u{1F310}'}</span>
            <a href="https://piparapp.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
              piparapp.com
            </a>
          </div>
        </div>
      </SettingsSection>

      {/* Copyright + description footer */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 4px' }}>
        <div>© {year} PiPar. All rights reserved.</div>
        <div style={{ marginTop: 4 }}>
          PiPar is your AI-powered digital business partner — voice, chat, tasks, calendar, notes, and email in one place.
        </div>
      </div>
    </div>
  )
}

// ── Reusable components ───────────────────────────────────────

function SettingsSection({ title, children }: { title: string; children: preact.ComponentChildren }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  )
}

function ToggleSwitch({ checked, onChange, disabled }: { checked: boolean; onChange: (val: boolean) => void; disabled?: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        border: 'none',
        background: checked ? '#2DA5A0' : 'var(--border)',
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        flexShrink: 0,
        transition: 'background 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        width: 18,
        height: 18,
        borderRadius: 9,
        background: '#fff',
        position: 'absolute',
        top: 3,
        left: checked ? 23 : 3,
        transition: 'left 0.15s',
      }} />
    </button>
  )
}

// ── Account Settings Panel ───────────────────────────────────────

const TIER_BADGE_COLORS: Record<string, string> = {
  free: '#9e9e9e',
  trial: '#ff9800',
  basic: '#4caf50',
  premium: '#2196f3',
  owner: '#9c27b0',
}

function formatCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Unit label for an action_type, mirroring _getItemUnit() in subscription_management_screen.dart */
function getActionUnit(actionType: string): string {
  switch (actionType) {
    case 'premium_chats':
    case 'chat_messages':
    case 'chat_basic':
    case 'chat_premium':
    case 'chat_genius':
    case 'voice_chat':
      return 'messages'
    case 'voice_minutes':
    case 'voice_processing':
      return 'minutes'
    case 'web_searches':
      return 'searches'
    case 'image_generations':
      return 'images'
    default:
      return 'items'
  }
}

function AccountSettings({ userEmail, onSignOut }: { userEmail: string; onSignOut?: () => void | Promise<void> }) {
  const [quota, setQuota] = useState<QuotaStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [couponCode, setCouponCode] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const [couponMsg, setCouponMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [showBreakdownDialog, setShowBreakdownDialog] = useState(false)

  const [currentMonthBreakdown, setCurrentMonthBreakdown] = useState<PeriodUsageBreakdown | null>(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)

  const loadQuota = useCallback(async () => {
    if (!userEmail) {
      setError('Not signed in')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const status = await getQuotaStatus(userEmail)
      if (status) setQuota(status)
      else setError('Failed to load account info')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [userEmail])

  const loadCurrentMonthBreakdown = useCallback(async () => {
    if (!userEmail) return
    setBreakdownLoading(true)
    try {
      const now = new Date()
      const data = await getUsageBreakdownForPeriod(userEmail, now.getFullYear(), now.getMonth() + 1)
      setCurrentMonthBreakdown(data)
    } catch {
      setCurrentMonthBreakdown(null)
    } finally {
      setBreakdownLoading(false)
    }
  }, [userEmail])

  useEffect(() => { loadQuota() }, [loadQuota])
  useEffect(() => { loadCurrentMonthBreakdown() }, [loadCurrentMonthBreakdown])

  const handleRedeem = useCallback(async () => {
    if (!couponCode.trim() || !userEmail) return
    setRedeeming(true)
    setCouponMsg(null)
    const result = await redeemCoupon(userEmail, couponCode)
    setCouponMsg({ ok: result.success, text: result.message })
    setRedeeming(false)
    if (result.success) {
      setCouponCode('')
      // Refresh quota to reflect newly added credits / extension
      loadQuota()
    }
  }, [couponCode, userEmail, loadQuota])

  const handleConfirmDelete = useCallback(async () => {
    if (!userEmail) return
    setDeleting(true)
    setDeleteError(null)
    const result = await deleteAccount(userEmail)
    if (result.ok) {
      // Sign out — auth state listener will route back to landing
      try { if (onSignOut) await onSignOut() } catch { /* ignore */ }
    } else {
      setDeleteError(result.error || 'Delete failed')
      setDeleting(false)
    }
  }, [userEmail, onSignOut])

  const tier = (quota?.subscription_tier || 'free').toLowerCase()
  const tierColor = TIER_BADGE_COLORS[tier] || '#9e9e9e'
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1)

  const pct = Math.max(0, Math.min(100, quota?.credit_percentage_left ?? 0))
  const breakdown = currentMonthBreakdown?.usage_breakdown
  const breakdownItems = (breakdown?.items || []).slice().sort((a, b) => b.total_credits - a.total_credits)
  const currentMonthLabel = currentMonthBreakdown?.period.label
    || `${MONTH_NAMES[new Date().getMonth()]} ${new Date().getFullYear()}`

  return (
    <div>
      <SettingsSection title="Signed in as">
        <div style={settingValueStyle}>{userEmail || '—'}</div>
      </SettingsSection>

      <SettingsSection title="Subscription">
        {loading && <div style={{ ...settingValueStyle, color: 'var(--text-muted)' }}>Loading…</div>}
        {error && !loading && <div style={{ ...settingValueStyle, color: '#f44336' }}>{error}</div>}
        {!loading && !error && quota && (
          <div style={settingValueStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: 12,
                background: `${tierColor}33`,
                color: tierColor,
                fontWeight: 600,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {tierLabel}
              </span>
              <button
                onClick={loadQuota}
                style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}
              >
                Refresh
              </button>
            </div>
          </div>
        )}
      </SettingsSection>

      {!loading && !error && quota && (
        <SettingsSection title="Credits Left">
          <div style={settingValueStyle}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)' }}>{formatCredits(quota.credits_left)}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{pct.toFixed(0)}% of monthly allotment</span>
            </div>
            <div style={{ width: '100%', height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: tierColor, transition: 'width 0.2s' }} />
            </div>
            {(quota.monthly_credits !== undefined || quota.deposit_credits !== undefined) && (
              <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                {quota.monthly_credits !== undefined && <span>Monthly: {formatCredits(quota.monthly_credits)}</span>}
                {quota.deposit_credits !== undefined && <span>Deposit: {formatCredits(quota.deposit_credits)}</span>}
              </div>
            )}
            {quota.warnings.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#ff9800' }}>
                {quota.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}
          </div>
        </SettingsSection>
      )}

      <SettingsSection title={`Usage Breakdown — ${currentMonthLabel}`}>
        <div style={{ ...settingValueStyle, padding: 0, overflow: 'hidden' }}>
          {!userEmail && (
            <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Sign in to see usage details.
            </div>
          )}
          {userEmail && breakdownLoading && breakdownItems.length === 0 && (
            <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          )}
          {userEmail && !breakdownLoading && breakdownItems.length === 0 && (
            <div style={{ padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No credits consumed yet this month.
            </div>
          )}
          {breakdownItems.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(250,250,250,0.05)' }}>
                  <th style={tableHeaderStyle}>Action</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Unit cost</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Credits</th>
                </tr>
              </thead>
              <tbody>
                {breakdownItems.map((item) => {
                  const unit = getActionUnit(item.action_type)
                  return (
                    <tr key={item.action_type} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={tableCellStyle}>{item.display_name || item.action_type}</td>
                      <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                        {item.count.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>{unit}</span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>{item.unit_cost.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>{item.total_credits.toFixed(0)}</td>
                    </tr>
                  )
                })}
              </tbody>
              {breakdown && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(250,250,250,0.05)' }}>
                    <td style={{ ...tableCellStyle, fontWeight: 600 }} colSpan={3}>Total credits used</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700, color: tierColor }}>
                      {formatCredits(breakdown.total_credits_used)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
        <button
          onClick={() => setShowBreakdownDialog(true)}
          disabled={!userEmail}
          style={{
            marginTop: 10,
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text)',
            cursor: userEmail ? 'pointer' : 'default',
            fontSize: 13,
            fontWeight: 500,
            opacity: userEmail ? 1 : 0.5,
          }}
        >
          View full breakdown by month…
        </button>
      </SettingsSection>

      {showBreakdownDialog && (
        <UsageBreakdownDialog
          userEmail={userEmail}
          tierColor={tierColor}
          onClose={() => setShowBreakdownDialog(false)}
        />
      )}

      <SettingsSection title="Redeem Coupon">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={couponCode}
            onInput={(e) => setCouponCode((e.target as HTMLInputElement).value)}
            placeholder="Coupon code"
            style={{ ...inputStyle, maxWidth: 260, textTransform: 'uppercase' }}
            disabled={redeeming}
          />
          <button
            onClick={handleRedeem}
            disabled={!couponCode.trim() || redeeming}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--primary)',
              color: '#fff',
              cursor: redeeming || !couponCode.trim() ? 'default' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: redeeming || !couponCode.trim() ? 0.6 : 1,
            }}
          >
            {redeeming ? 'Redeeming…' : 'Redeem'}
          </button>
        </div>
        {couponMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: couponMsg.ok ? '#4caf50' : '#f44336' }}>
            {couponMsg.text}
          </div>
        )}
      </SettingsSection>

      <SettingsSection title="Delete Account">
        {!confirmDelete ? (
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
              Permanently deletes your account and all cloud data (chats, notes, tasks, calendar, alerts, files). This cannot be undone.
            </div>
            <button
              onClick={() => { setConfirmDelete(true); setConfirmText(''); setDeleteError(null) }}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: '1px solid #f44336',
                background: 'transparent',
                color: '#f44336',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Delete Account
            </button>
          </div>
        ) : (
          <div style={{ ...settingValueStyle, borderColor: '#f44336' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#f44336', marginBottom: 8 }}>
              This will permanently delete {userEmail || 'your account'} and all data.
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
              Type <strong>DELETE</strong> below to confirm.
            </div>
            <input
              type="text"
              value={confirmText}
              onInput={(e) => setConfirmText((e.target as HTMLInputElement).value)}
              placeholder="DELETE"
              style={{ ...inputStyle, maxWidth: 260, marginBottom: 12 }}
              disabled={deleting}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={handleConfirmDelete}
                disabled={confirmText !== 'DELETE' || deleting}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#f44336',
                  color: '#fff',
                  cursor: confirmText === 'DELETE' && !deleting ? 'pointer' : 'default',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: confirmText === 'DELETE' && !deleting ? 1 : 0.5,
                }}
              >
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
              <button
                onClick={() => { setConfirmDelete(false); setConfirmText(''); setDeleteError(null) }}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: deleting ? 'default' : 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            </div>
            {deleteError && (
              <div style={{ marginTop: 10, fontSize: 13, color: '#f44336' }}>{deleteError}</div>
            )}
          </div>
        )}
      </SettingsSection>
    </div>
  )
}

// ── Usage Breakdown Dialog (per-month navigation) ─────────────────

function UsageBreakdownDialog({ userEmail, tierColor, onClose }: { userEmail: string; tierColor: string; onClose: () => void }) {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1) // 1-12
  const [period, setPeriod] = useState<PeriodUsageBreakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!userEmail) return
    setLoading(true)
    setError(null)
    try {
      const data = await getUsageBreakdownForPeriod(userEmail, year, month)
      if (data) setPeriod(data)
      else setError('Failed to load usage breakdown')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [userEmail, year, month])

  useEffect(() => { load() }, [load])

  const today = new Date()
  const isAtCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1) } else { setMonth(month - 1) }
  }
  const goNext = () => {
    if (isAtCurrentMonth) return
    if (month === 12) { setMonth(1); setYear(year + 1) } else { setMonth(month + 1) }
  }

  const periodLabel = period?.period.label || `${MONTH_NAMES[month - 1]} ${year}`
  const breakdown = period?.usage_breakdown
  const items = (breakdown?.items || []).slice().sort((a, b) => b.total_credits - a.total_credits)

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Usage Breakdown</span>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}
            title="Close"
          >
            {'\u{00D7}'}
          </button>
        </div>

        {/* Period navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderBottom: '1px solid var(--border)' }}>
          <button onClick={goPrev} disabled={loading} style={periodNavBtnStyle(loading)} title="Previous month">{'\u{2039}'}</button>
          <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
            {periodLabel}
          </div>
          <button onClick={goNext} disabled={isAtCurrentMonth || loading} style={periodNavBtnStyle(isAtCurrentMonth || loading)} title="Next month">{'\u{203A}'}</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}
          {!loading && error && <div style={{ padding: 24, textAlign: 'center', color: '#f44336', fontSize: 13 }}>{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No credits were consumed during this period.
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(250,250,250,0.05)' }}>
                  <th style={tableHeaderStyle}>Action</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Unit cost</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Credits</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const unit = getActionUnit(item.action_type)
                  return (
                    <tr key={item.action_type} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={tableCellStyle}>{item.display_name || item.action_type}</td>
                      <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                        {item.count.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>{unit}</span>
                      </td>
                      <td style={{ ...tableCellStyle, textAlign: 'right', color: 'var(--text-muted)' }}>{item.unit_cost.toFixed(2)}</td>
                      <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>{item.total_credits.toFixed(0)}</td>
                    </tr>
                  )
                })}
              </tbody>
              {breakdown && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(250,250,250,0.05)' }}>
                    <td style={{ ...tableCellStyle, fontWeight: 600 }} colSpan={3}>Total credits used</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700, color: tierColor }}>
                      {formatCredits(breakdown.total_credits_used)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

const periodNavBtnStyle = (disabled: boolean): Record<string, string | number> => ({
  width: 32,
  height: 32,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: disabled ? 'var(--text-muted)' : 'var(--text)',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 18,
  lineHeight: 1,
  opacity: disabled ? 0.4 : 1,
})

const tableHeaderStyle: Record<string, string | number> = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}

const tableCellStyle: Record<string, string | number> = {
  padding: '8px 12px',
  fontSize: 13,
  color: 'var(--text)',
}

const settingValueStyle: Record<string, string | number> = {
  fontSize: 14,
  color: 'var(--text-secondary)',
  padding: '10px 14px',
  background: 'var(--bg-surface)',
  borderRadius: 8,
  border: '1px solid var(--border)',
}

const inputStyle: Record<string, string | number> = {
  width: '100%',
  maxWidth: 400,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--text)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
}
